import { format } from 'date-fns'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'

import { Add, Delete, Edit, Group, Key, Schedule } from '@mui/icons-material'
import { Autocomplete, Box, Button, Card, IconButton, Link, MenuItem, Skeleton, Stack, TableContainer, TextField, Tooltip, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder, TableSortValues } from '../../../../../components/GRTable/GRTable'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import {
  useCreateInternalOrganizationMutation,
  useDeleteInternalOrganizationMutation,
  useGetInternalOrganizationsQuery,
  useGetInternalOrphanedUsersQuery,
  useUpdateInternalOrganizationMutation,
} from '../../../../api/organizations'
import { useGetInternalPackagesQuery, useGetInternalRolesQuery } from '../../../../api/plans'
import { OrphanedUsersDialog } from '../../../../components/OrphanedUsersDialog/OrphanedUsersDialog'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { getValidationErrorMessage } from '../../../../helpers/apiValidationError'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { packageTierLabelKeys, packageTiers, sortRoleNames } from '../../../plans/components/helpers/planHelpers'
import { PackageTier } from '../../../plans/types/Plans'
import { InternalOrganization, OrganizationSave, OrganizationStatus } from '../../types/Organizations'
import { GameSlotsCount } from '../GameSlotsCount/GameSlotsCount'
import { OrganizationEditDialog } from '../OrganizationEditDialog/OrganizationEditDialog'
import { OrganizationUsersDialog } from '../OrganizationUsersDialog/OrganizationUsersDialog'
import {
  filterOrganizations,
  getDaysUntil,
  getFirstExpiration,
  getOrganizationStatus,
  getUnstoredOrganizationFields,
  hasRole,
  isExpiringSoon,
  organizationFieldLabelKeys,
  OrganizationFormValues,
  organizationStatusFilters,
  organizationStatusLabelKeys,
  toOrganizationCreate,
  toOrganizationStatusFilter,
  toOrganizationUpdate,
} from '../helpers/organizationHelpers'

type CustomProps = {
  now: number
  role?: string
  onEdit: (organization: InternalOrganization) => void
  onListUsers: (organization: InternalOrganization) => void
  onDelete: (organization: InternalOrganization) => void
}

const formatExpiration = (timestamp: number) => format(timestamp, 'dd.MM.yyyy, HH:mm')

const NEW_ORGANIZATION = 'new'

export const OrganizationsList: FC = () => {
  const { t } = useTranslation()
  const { notify } = useMutationFeedback()
  const [searchParams, setSearchParams] = useSearchParams()
  const organizationsQuery = useGetInternalOrganizationsQuery()
  const packagesQuery = useGetInternalPackagesQuery()
  const rolesQuery = useGetInternalRolesQuery()
  const orphansQuery = useGetInternalOrphanedUsersQuery()
  const [createOrganization, { isLoading: isCreating }] = useCreateInternalOrganizationMutation()
  const [updateOrganization, { isLoading: isUpdating }] = useUpdateInternalOrganizationMutation()
  const [deleteOrganization] = useDeleteInternalOrganizationMutation()
  const [isReadingBeforeSave, setIsReadingBeforeSave] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<ConfirmDialogData<InternalOrganization>>()
  const [edited, setEdited] = useState<InternalOrganization>()
  const [now] = useState(Date.now)

  const organizations = useMemo(() => organizationsQuery.data ?? [], [organizationsQuery.data])
  const catalog = useMemo(() => packagesQuery.data ?? [], [packagesQuery.data])
  const roleNames = useMemo(() => sortRoleNames((rolesQuery.data ?? []).map(({ name }) => name)), [rolesQuery.data])

  const search = searchParams.get('search') ?? ''
  const status = toOrganizationStatusFilter(searchParams.get('status'))
  const role = searchParams.get('role') || undefined
  const tierParam = searchParams.get('tier')
  const tier = tierParam !== null && packageTiers.includes(Number(tierParam)) ? (Number(tierParam) as PackageTier) : undefined
  const editParam = searchParams.get('edit')
  const usersOrganization = organizations.find(({ id }) => id === searchParams.get('users'))
  const orphansOpen = searchParams.get('orphans') === '1'

  const rows = useMemo(() => filterOrganizations(organizations, { search, status, role, tier }), [organizations, search, status, role, tier])

  useEffect(() => {
    if (!editParam || editParam === NEW_ORGANIZATION) setEdited(undefined)
    else if (edited?.id !== editParam) setEdited(organizations.find(({ id }) => id === editParam))
  }, [editParam, organizations, edited?.id])

  const dialogOpen = editParam === NEW_ORGANIZATION || (!!edited && edited.id === editParam)

  const setParam = useCallback(
    (name: string, value?: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (value) next.set(name, value)
          else next.delete(name)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const failureMessage = useCallback(
    (key: string, error: unknown) => {
      const reason = getValidationErrorMessage(error)
      return reason ? `${t(key)}: ${reason}` : t(key)
    },
    [t]
  )

  const tableColumns: GRTableColumn<InternalOrganization, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-csm:organizations_name'),
        accessor: ({ row }) => (
          <Stack>
            {row.name ? (
              <Typography fontWeight="bold">{row.name}</Typography>
            ) : (
              <Typography color="text.secondary">{t('internal-csm:organizations_no_name')}</Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {row.id}
            </Typography>
          </Stack>
        ),
        sortable: true,
        sortAccessor: ({ row }) => (row.name ?? '').toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 150 } },
      },
      {
        labelAccessor: t('internal-csm:organizations_users'),
        accessor: ({ row }) => (row.maxSeats > 0 ? `${row.userCount}/${row.maxSeats}` : String(row.userCount)),
        sortable: true,
        sortAccessor: ({ row }) => row.userCount,
        headerCellProps: { sx: { minWidth: 64 } },
      },
      {
        labelAccessor: t('internal-csm:organizations_status'),
        accessor: ({ row }) => {
          const organizationStatus = getOrganizationStatus(row)
          return (
            <Typography variant="body2" color={organizationStatus === OrganizationStatus.Inactive ? 'error.main' : undefined}>
              {t(organizationStatusLabelKeys[organizationStatus])}
            </Typography>
          )
        },
        sortable: true,
        sortAccessor: ({ row }) => getOrganizationStatus(row),
        headerCellProps: { sx: { minWidth: 90 } },
      },
      {
        labelAccessor: t('internal-csm:organizations_expiration'),
        accessor: ({ row, customTableProps }) => {
          const expiration = getFirstExpiration(row)
          if (expiration === undefined) return t('internal-csm:organizations_continuous')
          const currentTime = customTableProps?.now ?? Date.now()
          return (
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
              {isExpiringSoon(expiration, currentTime) && (
                <Tooltip title={t('internal-csm:organizations_expires_in', { count: getDaysUntil(expiration, currentTime) })}>
                  <Schedule fontSize="small" color="warning" aria-label={t('internal-csm:organizations_expires_soon')} />
                </Tooltip>
              )}
              <Typography variant="body2" color={expiration < currentTime ? 'error.main' : undefined} whiteSpace="nowrap">
                {formatExpiration(expiration)}
              </Typography>
            </Stack>
          )
        },
        sortable: true,
        sortAccessor: ({ row }) => getFirstExpiration(row) ?? TableSortValues.AlwaysLast,
        sortOrder: SortOrder.ASC,
        defaultSortOrder: SortOrder.ASC,
        headerCellProps: { sx: { minWidth: 140 } },
      },
      {
        labelAccessor: t('internal-csm:organizations_plans'),
        accessor: ({ row, customTableProps }) =>
          row.packages.length ? (
            <Stack>
              {row.packages.map((planPackage, index) => {
                const highlighted = hasRole(planPackage, customTableProps?.role)
                return (
                  <Typography
                    key={`${planPackage.packageId}-${index}`}
                    variant="body2"
                    fontWeight={highlighted ? 'bold' : undefined}
                    color={highlighted ? 'primary.main' : undefined}
                  >
                    {planPackage.name} ({planPackage.tier})
                    {highlighted &&
                      ` / ${t('internal-csm:organizations_expires', {
                        date: planPackage.expiration ? formatExpiration(planPackage.expiration) : t('internal-csm:organizations_continuous'),
                      })}`}
                  </Typography>
                )
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="error.main">
              {t('internal-csm:organizations_no_plans')}
            </Typography>
          ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 140 } },
      },
      {
        labelAccessor: t('internal-csm:organizations_limits'),
        accessor: ({ row }) => (
          <Stack whiteSpace="nowrap">
            <GameSlotsCount organization={row} />
            <Typography variant="body2">
              {t('internal-csm:organizations_followed_games', {
                count: row.followedGames.length,
                max: row.maxFollowedGames > 0 ? row.maxFollowedGames : '∞',
              })}
            </Typography>
          </Stack>
        ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 164 } },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => (
          <Box display="flex" justifyContent="flex-end">
            <Tooltip title={t('internal-csm:organizations_edit')}>
              <IconButton size="small" color="primary" aria-label={t('internal-csm:organizations_edit')} onClick={() => customTableProps?.onEdit(row)}>
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('internal-csm:organizations_list_users')}>
              <IconButton
                size="small"
                color="primary"
                aria-label={t('internal-csm:organizations_list_users')}
                onClick={() => customTableProps?.onListUsers(row)}
              >
                <Group />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('internal-csm:organization_access_open')}>
              <IconButton
                size="small"
                color="primary"
                aria-label={t('internal-csm:organization_access_open')}
                component={RouterLink}
                to={`/internal/organizations/${row.id}/access/reports`}
              >
                <Key />
              </IconButton>
            </Tooltip>
            <Tooltip title={row.userCount > 0 ? t('internal-csm:organizations_delete_has_users') : t('internal-csm:organizations_delete')}>
              <span>
                <IconButton
                  size="small"
                  aria-label={t('internal-csm:organizations_delete')}
                  color="error"
                  disabled={row.userCount > 0}
                  onClick={() => customTableProps?.onDelete(row)}
                >
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
        cellProps: { align: 'right', sx: { px: 1 } },
        headerCellProps: { sx: { minWidth: 152 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      now,
      role,
      onEdit: (organization) => setParam('edit', organization.id),
      onListUsers: (organization) => setParam('users', organization.id),
      onDelete: (organization) =>
        setDeleteConfirm({
          title: t('internal-csm:organizations_delete'),
          confirmText: t('internal-csm:organizations_delete_confirm', { name: organization.name || organization.id }),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: organization,
        }),
    }),
    [now, role, setParam, t]
  )

  const closeDialog = () => setParam('edit', null)

  const isSaving = isReadingBeforeSave || isCreating || isUpdating

  // The cached list can be stale: users join organizations and follow games without this page knowing.
  const readOrganization = async (organizationId: string) => {
    const fresh = await organizationsQuery.refetch().unwrap()
    return fresh.find(({ id }) => id === organizationId)
  }

  const handleSave = async (values: OrganizationFormValues) => {
    if (isSaving) return
    try {
      let sent: OrganizationSave
      let stored: InternalOrganization | undefined
      if (edited) {
        setIsReadingBeforeSave(true)
        const fresh = await readOrganization(edited.id).finally(() => setIsReadingBeforeSave(false))
        if (!fresh) {
          notify(t('internal-csm:organizations_no_longer_listed', { name: edited.name || edited.id }), 'error')
          return
        }
        const update = toOrganizationUpdate(fresh, values, edited)
        sent = update
        stored = (await updateOrganization(update).unwrap()).stored
      } else {
        const create = toOrganizationCreate(values)
        sent = create
        stored = (await createOrganization(create).unwrap()).stored
      }
      if (!stored) {
        notify(t('internal-csm:organizations_not_found_after_save', { name: sent.name }), 'error')
        return
      }
      const unstored = getUnstoredOrganizationFields(sent, stored)
      if (unstored.length) {
        const labels = Array.from(new Set(unstored.map((field) => t(organizationFieldLabelKeys[field]))))
        notify(t('internal-csm:organizations_not_stored', { fields: labels.join(', ') }), 'error')
        return
      }
      notify(t(edited ? 'internal-csm:organizations_updated' : 'internal-csm:organizations_added', { name: sent.name }), 'success')
      closeDialog()
    } catch (error) {
      notify(failureMessage('internal-csm:organizations_save_failed', error), 'error')
    }
  }

  const handleDeleteConfirm = async (confirmed?: ConfirmDialogData<InternalOrganization>) => {
    setDeleteConfirm(undefined)
    const organization = confirmed?.data
    if (!organization) return
    const name = organization.name || organization.id
    try {
      const fresh = await readOrganization(organization.id)
      if (!fresh) {
        notify(t('internal-csm:organizations_no_longer_listed', { name }), 'error')
        return
      }
      if (fresh.userCount > 0) {
        notify(t('internal-csm:organizations_delete_has_users_now', { name, count: fresh.userCount }), 'error')
        return
      }
      const { stored } = await deleteOrganization(organization.id).unwrap()
      if (stored) {
        notify(t('internal-csm:organizations_not_deleted', { name }), 'error')
        return
      }
      notify(t('internal-csm:organizations_deleted', { name }), 'success')
    } catch (error) {
      notify(failureMessage('internal-csm:organizations_delete_failed', error), 'error')
    }
  }

  const isLoading = organizationsQuery.isLoading || packagesQuery.isLoading || rolesQuery.isLoading
  const isError = organizationsQuery.isError || packagesQuery.isError || rolesQuery.isError
  const orphanCount = orphansQuery.data?.length

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
        <TextField
          size="small"
          type="search"
          label={t('internal-csm:organizations_search')}
          value={search}
          onChange={(event) => setParam('search', event.target.value)}
          sx={{ minWidth: 280 }}
        />
        <TextField
          select
          size="small"
          label={t('internal-csm:organizations_status_filter')}
          value={status}
          onChange={(event) => setParam('status', event.target.value)}
          sx={{ minWidth: 200 }}
        >
          {organizationStatusFilters.map((filter) => (
            <MenuItem key={filter} value={filter}>
              {t(`internal-csm:organizations_show_${filter.replace('-', '_')}`)}
            </MenuItem>
          ))}
        </TextField>
        <Autocomplete
          size="small"
          options={roleNames}
          value={role ?? null}
          onChange={(_, value) => setParam('role', value)}
          renderInput={(params) => <TextField {...params} label={t('internal-csm:organizations_role_filter')} placeholder={t('internal-csm:plans_any_role')} />}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select
          size="small"
          label={t('internal-csm:plans_tier')}
          value={tier ?? 'any'}
          onChange={(event) => setParam('tier', event.target.value === 'any' ? null : event.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="any">{t('internal-csm:organizations_any_tier')}</MenuItem>
          {packageTiers.map((packageTier) => (
            <MenuItem key={packageTier} value={packageTier}>
              {t(packageTierLabelKeys[packageTier])}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" color="text.secondary">
          {t('internal-csm:organizations_count', { count: rows.length, total: organizations.length })}
        </Typography>
        <Box flexGrow={1} />
        <Button variant="contained" startIcon={<Add />} onClick={() => setParam('edit', NEW_ORGANIZATION)} disabled={isLoading || isError}>
          {t('internal-csm:organizations_add_organization')}
        </Button>
      </Stack>
      <Typography variant="body2" mb={2}>
        {t('internal-csm:organizations_orphaned_users')}{' '}
        {orphansQuery.isLoading ? (
          <Skeleton component="span" width={24} sx={{ display: 'inline-block' }} />
        ) : orphansQuery.isError ? (
          <Typography component="span" variant="body2" color="error.main">
            {t('internal-csm:organizations_orphaned_users_failed')}
          </Typography>
        ) : orphanCount ? (
          <Link component="button" variant="body2" onClick={() => setParam('orphans', '1')} aria-label={t('internal-csm:organizations_orphaned_users_open')}>
            {orphanCount}
          </Link>
        ) : (
          0
        )}
      </Typography>

      <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-csm:organizations_load_failed')} skeletonHeight={480}>
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <OrganizationEditDialog open={dialogOpen} organization={edited} catalog={catalog} isSaving={isSaving} onClose={closeDialog} onSave={handleSave} />
      <ConfirmDialog open={!!deleteConfirm} confirmDialogData={deleteConfirm} onClose={handleDeleteConfirm} />
      <OrganizationUsersDialog open={!!usersOrganization} organization={usersOrganization} onClose={() => setParam('users', null)} />
      <OrphanedUsersDialog open={orphansOpen} onClose={() => setParam('orphans', null)} />
    </Box>
  )
}
